import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Textarea } from '../components/Textarea';
import { UserIcon, ArchiveIcon, TrashIcon, AlertTriangleIcon, ArrowLeftIcon } from '../components/Icons';

interface Message {
  id: number;
  senderId: string;
  senderName: string;
  senderPhoto: string;
  content: string;
  timestamp: string;
  isCurrentUser: boolean;
}

export const ConversationThreadPage = () => {
  const conversationId = window.location.pathname.match(/\/messages\/(\d+)/)?.[1];
  const [messageText, setMessageText] = useState('');
  const [showOptions, setShowOptions] = useState(false);

  // Mock data
  const participant = {
    id: '1',
    name: 'Sarah Johnson',
    role: 'Agent',
    photo: '',
  };

  const conversationSubject = 'Re: Manuscript Revisions';

  const messages: Message[] = [
    {
      id: 1,
      senderId: 'current',
      senderName: 'You',
      senderPhoto: '',
      content: 'Hi Sarah! Thank you so much for requesting my full manuscript. I\'ve attached the revised version based on your initial feedback. I focused on tightening the opening chapter and developing the main character\'s motivation more clearly.',
      timestamp: '2024-03-10T09:00:00Z',
      isCurrentUser: true,
    },
    {
      id: 2,
      senderId: '1',
      senderName: 'Sarah Johnson',
      senderPhoto: '',
      content: 'Thanks for sending this over! I\'m excited to dive in. Just to confirm - this is the full 85,000 word manuscript, correct?',
      timestamp: '2024-03-10T10:30:00Z',
      isCurrentUser: false,
    },
    {
      id: 3,
      senderId: 'current',
      senderName: 'You',
      senderPhoto: '',
      content: 'Yes, that\'s correct! It\'s 85,432 words to be exact. I\'ve also included a brief cover letter at the beginning summarizing the changes I made based on your feedback.',
      timestamp: '2024-03-10T11:15:00Z',
      isCurrentUser: true,
    },
    {
      id: 4,
      senderId: '1',
      senderName: 'Sarah Johnson',
      senderPhoto: '',
      content: 'Perfect! I appreciate you being so responsive to feedback. I\'ll aim to get back to you within 6-8 weeks. In the meantime, feel free to reach out if you have any questions.',
      timestamp: '2024-03-10T14:20:00Z',
      isCurrentUser: false,
    },
    {
      id: 5,
      senderId: 'current',
      senderName: 'You',
      senderPhoto: '',
      content: 'That sounds great! I really appreciate you taking the time to read it. Looking forward to hearing your thoughts.',
      timestamp: '2024-03-10T15:00:00Z',
      isCurrentUser: true,
    },
    {
      id: 6,
      senderId: '1',
      senderName: 'Sarah Johnson',
      senderPhoto: '',
      content: 'Thanks for sending the revised pages. I\'ll take a look this week!',
      timestamp: '2024-03-12T09:00:00Z',
      isCurrentUser: false,
    },
  ];

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hours ago`;
    } else if (diffInHours < 48) {
      return '1 day ago';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const handleSendMessage = () => {
    if (!messageText.trim()) {
      window.alert('Please enter a message');
      return;
    }

    console.log('Sending message:', messageText);
    window.alert('Message sent!');
    setMessageText('');
  };

  const handleArchive = () => {
    if (window.confirm('Archive this conversation?')) {
      console.log('Archiving conversation:', conversationId);
      window.alert('Conversation archived');
      window.location.href = '/messages';
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      console.log('Deleting conversation:', conversationId);
      window.alert('Conversation deleted');
      window.location.href = '/messages';
    }
  };

  const handleReport = () => {
    if (window.confirm('Report this conversation for inappropriate content?')) {
      console.log('Reporting conversation:', conversationId);
      window.alert('Conversation reported. Our team will review it.');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Header />

      {/* Conversation Header */}
      <div style={{
        backgroundColor: theme.colors.white,
        borderBottom: `1px solid ${theme.colors.border}`,
        padding: '20px 40px',
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              variant="ghost"
              size="small"
              onClick={() => window.location.href = '/messages'}
            >
              ← Back
            </Button>

            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: theme.colors.backgroundAlt,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <UserIcon width={24} height={24} />
            </div>

            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '500',
                  color: theme.colors.textPrimary,
                  margin: 0,
                }}>
                  {participant.name}
                </h2>
                <Badge variant="primary">{participant.role}</Badge>
              </div>
              <div style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
              }}>
                {conversationSubject}
              </div>
            </div>
          </div>

          <div style={{ position: 'relative' }}>
            <Button
              variant="ghost"
              size="small"
              onClick={() => setShowOptions(!showOptions)}
            >
              ⋯
            </Button>

            {showOptions && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                backgroundColor: theme.colors.white,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.medium,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                minWidth: '180px',
                zIndex: 100,
              }}>
                <button
                  onClick={() => {
                    handleArchive();
                    setShowOptions(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: theme.colors.textPrimary,
                    borderBottom: `1px solid ${theme.colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <ArchiveIcon width={14} height={14} />
                  Archive Conversation
                </button>
                <button
                  onClick={() => {
                    handleDelete();
                    setShowOptions(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: theme.colors.error,
                    borderBottom: `1px solid ${theme.colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <TrashIcon width={14} height={14} />
                  Delete Conversation
                </button>
                <button
                  onClick={() => {
                    handleReport();
                    setShowOptions(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: theme.colors.error,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <AlertTriangleIcon width={14} height={14} />
                  Report Conversation
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <main style={{
        flex: 1,
        maxWidth: '900px',
        margin: '0 auto',
        width: '100%',
        padding: '40px',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}>
          {messages.map((message) => (
            <div
              key={message.id}
              style={{
                display: 'flex',
                gap: '16px',
                flexDirection: message.isCurrentUser ? 'row-reverse' : 'row',
              }}
            >
              {/* Avatar */}
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: theme.colors.backgroundAlt,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <UserIcon width={24} height={24} />
              </div>

              {/* Message Content */}
              <div style={{
                flex: 1,
                maxWidth: '70%',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '8px',
                  flexDirection: message.isCurrentUser ? 'row-reverse' : 'row',
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                  }}>
                    {message.senderName}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: theme.colors.textTertiary,
                  }}>
                    {formatTimestamp(message.timestamp)}
                  </div>
                </div>

                <div style={{
                  padding: '16px 20px',
                  borderRadius: theme.borderRadius.medium,
                  backgroundColor: message.isCurrentUser ? theme.colors.primary : theme.colors.white,
                  color: message.isCurrentUser ? theme.colors.white : theme.colors.textPrimary,
                  border: message.isCurrentUser ? 'none' : `1px solid ${theme.colors.border}`,
                  fontSize: '15px',
                  lineHeight: '1.6',
                }}>
                  {message.content}
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Message Input - Fixed at Bottom */}
      <div style={{
        backgroundColor: theme.colors.white,
        borderTop: `1px solid ${theme.colors.border}`,
        padding: '20px 40px',
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
        }}>
          <Card>
            <Textarea
              label=""
              placeholder="Type your message..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              rows={4}
            />

            <div style={{
              marginTop: '16px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}>
              <Button
                variant="ghost"
                onClick={() => setMessageText('')}
                disabled={!messageText.trim()}
              >
                Clear
              </Button>
              <Button
                onClick={handleSendMessage}
                disabled={!messageText.trim()}
              >
                Send Message
              </Button>
            </div>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};
